import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  ArrowDownToLine,
  ArrowLeft,
  ArrowUpRight,
  CalendarDays,
  Check,
  CheckCheck,
  ChevronRight,
  ClipboardList,
  FileText,
  Flag,
  Layers,
  Loader2,
  Megaphone,
  Plus,
  RefreshCw,
  Settings2,
  Target,
  X,
} from 'lucide-react';
import type { Campaign, Content, State } from '../shared/model';
import { stageLabels } from '../shared/model';
import {
  operationalCalendar,
  type BusinessEvent,
  type CampaignGoal,
  type CampaignOperations,
  type Material,
  type ProductionTask,
  type PublicationPackage,
} from '../shared/campaigns';
import { FormatBadge } from './identity';
import { PromotionWorkspace } from './PromotionWorkspace';
import { ReadingHistory } from './ReadingHistory';
import { AdsWorkspace } from './AdsWorkspace';
import { DistributionWorkspace } from './DistributionWorkspace';
import { AutomationWorkspace } from './AutomationWorkspace';
import { PackageDestinations } from './PackageDestinations';
import { ManualMetricsWorkspace } from './ManualMetricsWorkspace';
import { ArchiveHistoryWorkspace } from './ArchiveHistoryWorkspace';
import { assessPackageCta, guideExceptionEligible } from '../shared/package-review';
import { PackageCtaReview, PackageRevisionDiff } from './PackageReview';
import './campaigns.css';

type Props = {
  campaign: Campaign;
  initialTab?: string;
  initialPackageId?: string;
  initialAutomationId?: string;
  data: State;
  onBack: () => void;
  onEdit: () => void;
  onContent: (content: Content) => void;
  onNewContent: () => void;
  onAddPublication: () => void;
  onNewCampaign?: () => void;
  collectionSettings?: ReactNode;
  refresh: () => Promise<unknown>;
};
type Tab =
  | 'contents'
  | 'production'
  | 'materials'
  | 'distribution'
  | 'automations'
  | 'promotions'
  | 'results';
type Editor =
  | { kind: 'task'; item?: ProductionTask }
  | { kind: 'material'; item?: Material }
  | { kind: 'package'; item?: PublicationPackage }
  | { kind: 'decision'; item: PublicationPackage; decision: 'aprovada' | 'ajustes' | 'recusada' }
  | { kind: 'event'; item?: BusinessEvent }
  | { kind: 'goal'; item?: CampaignGoal }
  | { kind: 'link' };
const taskLabels: Record<ProductionTask['status'], string> = {
  pendente: 'Pendente',
  em_andamento: 'Em andamento',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
};
const packageLabels: Record<PublicationPackage['status'], string> = {
  em_revisao: 'Em revisão',
  aprovada: 'Aprovado',
  ajustes: 'Ajustes pedidos',
  recusada: 'Recusado',
  substituida: 'Substituído',
};
const materialLabels: Record<Material['kind'], string> = {
  roteiro: 'Roteiro',
  legenda: 'Legenda',
  midia: 'Mídia',
};
const eventLabels: Record<BusinessEvent['kind'], string> = {
  pedido: 'Pedidos de guia',
  qualificada: 'Leads qualificados',
  conversa: 'Conversas',
  demonstracao: 'Demonstrações',
  oportunidade: 'Oportunidades',
};
const campaignLabels: Record<Campaign['status'], string> = {
  planejada: 'Planejada',
  ativa: 'Ativa',
  pausada: 'Pausada',
  encerrada: 'Encerrada',
};
const dateLabel = (value: string) =>
  value ? new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : 'Sem data';
const stamp = (value: string) =>
  value
    ? new Date(value).toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Sem leitura';
const s = (form: FormData, name: string) => String(form.get(name) ?? '').trim();
const count = (value: number) => value.toLocaleString('pt-BR');
const externalUrl = (value: string) => {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
};
const contentName = (content: Content) =>
  `${content.flgId ? `${content.flgId} · ` : ''}${content.title}`;

function Field({
  label,
  children,
  wide = false,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <label className={`field ${wide ? 'wide' : ''}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}
function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="cw-empty">
      <FileText size={25} />
      <p>{children}</p>
    </div>
  );
}
function Status({ value, children }: { value: string; children: ReactNode }) {
  return <span className={`cw-status cw-status-${value}`}>{children}</span>;
}
function ExternalLink({ url, children }: { url: string; children: ReactNode }) {
  const href = externalUrl(url);
  return href ? (
    <a href={href} target="_blank" rel="noreferrer">
      {children}
      <ArrowUpRight size={13} />
    </a>
  ) : null;
}
function Dialog({
  title,
  children,
  busy,
  onClose,
}: {
  title: string;
  children: ReactNode;
  busy: boolean;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => element?.close();
  }, []);
  return (
    <dialog
      ref={dialog}
      className="cw-dialog"
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
    >
      <div className="cw-dialog-heading">
        <h2 id={titleId}>{title}</h2>
        <button
          type="button"
          className="cw-icon-button"
          aria-label="Fechar formulário"
          disabled={busy}
          onClick={onClose}
        >
          <X size={19} />
        </button>
      </div>
      {children}
    </dialog>
  );
}

export function CampaignWorkspace({
  campaign,
  initialTab,
  initialPackageId,
  initialAutomationId,
  data,
  onBack,
  onEdit,
  onContent,
  onNewContent,
  onAddPublication,
  onNewCampaign,
  collectionSettings,
  refresh,
}: Props) {
  const validTab = (value?: string): Tab =>
    [
      'contents',
      'production',
      'materials',
      'distribution',
      'automations',
      'promotions',
      'results',
    ].includes(value ?? '')
      ? (value as Tab)
      : 'contents';
  const [tab, setTab] = useState<Tab>(() => validTab(initialTab));
  const [operations, setOperations] = useState<CampaignOperations>();
  const [editor, setEditor] = useState<Editor>();
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [taskFilter, setTaskFilter] = useState('open');
  const [selectedContent, setSelectedContent] = useState('');
  const [materialKind, setMaterialKind] = useState<Material['kind']>('roteiro');
  const loadSequence = useRef(0);
  const canEdit = data.permissions.edit;
  const canApprove =
    data.permissions.approve &&
    (data.permissions.admin || campaign.approverMemberId === data.currentMemberId);
  const contents = data.contents.filter((content) => content.campaignIds.includes(campaign.id));
  const calendar = operationalCalendar(data);
  const titleFor = (id: string | null) => {
    if (!id) return 'Campanha';
    // A publication can belong to the campaign without assigning its whole piece to it.
    const content = data.contents.find((item) => item.id === id);
    return content ? contentName(content) : 'Peça indisponível';
  };
  const contentOptions = contents.map((content) => (
    <option key={content.id} value={content.id}>
      {contentName(content)}
    </option>
  ));
  const materials = operations?.materials ?? [];
  const replaced = new Set(materials.map((material) => material.replacesId).filter(Boolean));
  const currentMaterials = materials.filter((material) => !replaced.has(material.id));
  const selectedInRevision = (material: Material, pkg?: PublicationPackage) => {
    if (!pkg) return true;
    let version: Material | undefined = material;
    while (version) {
      if (pkg.materialIds.includes(version.id)) return true;
      const previousId: string | null = version.replacesId;
      version = previousId ? materials.find((item) => item.id === previousId) : undefined;
    }
    return false;
  };
  const activeTasks =
    operations?.tasks.filter((task) => !['concluida', 'cancelada'].includes(task.status)) ?? [];
  const overdue = activeTasks.filter((task) => task.dueDate && task.dueDate < data.today);
  const pendingPackages = operations?.packages.filter((item) => item.status === 'em_revisao') ?? [];
  const publicationOnly =
    operations?.results.media.filter(
      (media) => !contents.some((content) => content.id === media.contentId),
    ) ?? [];
  const hasWork =
    contents.length > 0 ||
    !!operations?.results.publications ||
    !!activeTasks.length ||
    !!materials.length;

  async function load() {
    const sequence = ++loadSequence.current;
    const response = await fetch(`/api/campaigns/${encodeURIComponent(campaign.id)}/operations`);
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Não foi possível carregar a campanha.');
    if (sequence === loadSequence.current) setOperations(result as CampaignOperations);
  }
  useEffect(() => {
    setLoading(true);
    setOperations(undefined);
    setError('');
    setEditor(undefined);
    setTab(validTab(initialTab));
    load()
      .catch((reason) => setError(reason.message))
      .finally(() => setLoading(false));
    return () => {
      loadSequence.current++;
    };
  }, [campaign.id]);
  useEffect(() => setTab(validTab(initialTab)), [initialTab]);

  async function mutate(path: string, method: 'POST' | 'PUT' | 'DELETE', body: unknown) {
    const response = await fetch(path, {
      method,
      headers: { 'Content-Type': 'application/json', 'X-FLG-CSRF': data.csrf },
      body: JSON.stringify(body),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (response.status === 409) await Promise.all([load(), refresh()]);
      throw new Error(result.error || 'Não foi possível salvar. Tente novamente.');
    }
    return result;
  }
  async function save(action: () => Promise<unknown>, message: string) {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await action();
      setEditor(undefined);
      await Promise.all([load(), refresh()]);
      setNotice(message);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Não foi possível salvar.');
    } finally {
      setBusy(false);
    }
  }
  function open(next: Editor) {
    setError('');
    setNotice('');
    setSelectedContent(
      next.kind === 'package' || next.kind === 'material'
        ? (next.item?.contentId ?? contents[0]?.id ?? '')
        : '',
    );
    setMaterialKind(next.kind === 'material' ? (next.item?.kind ?? 'roteiro') : 'roteiro');
    setEditor(next);
  }
  const submit =
    (handler: (form: FormData) => Promise<unknown>, message: string) =>
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const form = new FormData(event.currentTarget);
      void save(() => handler(form), message);
    };
  const buttons = (label: string) => (
    <div className="cw-form-actions">
      <button type="button" disabled={busy} onClick={() => setEditor(undefined)}>
        Cancelar
      </button>
      <button className="primary" disabled={busy} type="submit">
        {busy ? <Loader2 className="spin" size={16} /> : <Check size={16} />}
        {label}
      </button>
    </div>
  );

  async function saveMaterial(form: FormData) {
    const file = form.get('file');
    let dataBase64: string | undefined;
    if (file instanceof File && file.size) {
      if (file.size > 15 * 1024 * 1024) throw new Error('O arquivo deve ter no máximo 15 MB.');
      dataBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
        reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.readAsDataURL(file);
      });
    }
    return mutate('/api/materials', 'POST', {
      campaignId: campaign.id,
      contentId: s(form, 'contentId'),
      title: s(form, 'title'),
      kind: s(form, 'kind'),
      text: s(form, 'text'),
      url: s(form, 'url'),
      fileName: file instanceof File && file.size ? file.name : s(form, 'fileName'),
      mime: file instanceof File && file.size ? file.type || 'application/octet-stream' : '',
      ...(dataBase64 ? { dataBase64 } : {}),
      sha256: s(form, 'sha256'),
      replacesId: editor?.kind === 'material' ? (editor.item?.id ?? null) : null,
    });
  }

  return (
    <div className="campaign-workspace">
      <button className="text-button cw-back" onClick={onBack}>
        <ArrowLeft size={15} />
        Todas as campanhas
      </button>
      <header className="cw-heading">
        <div>
          <div className="cw-eyebrow">
            <Flag size={14} />
            CAMPANHA <Status value={campaign.status}>{campaignLabels[campaign.status]}</Status>
          </div>
          <h1>{campaign.name}</h1>
          <p className="cw-campaign-meta">
            {campaign.owner || 'Responsável a definir'} · {dateLabel(campaign.startDate)}
            {campaign.endDate ? ` → ${dateLabel(campaign.endDate)}` : ' · Sem data de término'}
          </p>
        </div>
        <div className="cw-actions cw-heading-actions">
          {canEdit && hasWork && (
            <button className="primary" onClick={onAddPublication}>
              <Plus size={16} />
              Adicionar vídeo do Instagram
            </button>
          )}
          <button
            aria-label="Atualizar campanha"
            disabled={busy || loading}
            onClick={() => {
              setLoading(true);
              setError('');
              void Promise.all([load(), refresh()])
                .catch((reason) => setError(reason.message))
                .finally(() => setLoading(false));
            }}
          >
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </button>
          {canEdit && <button onClick={onEdit}>Editar campanha</button>}
          {canEdit && onNewCampaign && (
            <details className="cw-more">
              <summary>Mais opções</summary>
              <button onClick={onNewCampaign}>Criar outra campanha</button>
            </details>
          )}
        </div>
      </header>
      <details className="cw-strategy">
        <summary>Estratégia da campanha</summary>
        <p className="cw-objective">
          {campaign.objective || 'Defina o objetivo para orientar as entregas da campanha.'}
        </p>
        <div className="cw-brief">
          <div>
            <span>Público</span>
            <strong>{campaign.audience || 'A definir'}</strong>
          </div>
          <div>
            <span>Chamada para ação</span>
            <strong>{campaign.cta || 'A definir'}</strong>
          </div>
          <div>
            <span>Período</span>
            <strong>
              {dateLabel(campaign.startDate)} → {dateLabel(campaign.endDate)}
            </strong>
          </div>
          <div>
            <span>Responsável · Aprovador</span>
            <strong>
              {campaign.owner || 'A definir'} ·{' '}
              {data.members.find((member) => member.id === campaign.approverMemberId)?.name ||
                campaign.approver ||
                'A definir'}
            </strong>
          </div>
        </div>
        {campaign.notes && (
          <div className="cw-notes">
            <strong>Notas e contexto</strong>
            <p>{campaign.notes}</p>
          </div>
        )}
      </details>
      {campaign.strategyReviewRequired && (
        <div className="cw-info">
          A estratégia foi atualizada. Confira os materiais e envie uma nova revisão dos pacotes de
          publicação afetados.
        </div>
      )}
      {hasWork && (
        <div className="cw-summary">
          <div>
            <strong>{contents.length}</strong>
            <span>{contents.length === 1 ? 'conteúdo na campanha' : 'conteúdos na campanha'}</span>
          </div>
          <div>
            <strong>{activeTasks.length}</strong>
            <span>
              {activeTasks.length === 1 ? 'tarefa aberta' : 'tarefas abertas'}
              {overdue.length > 0 && <em> · {overdue.length} em atraso</em>}
            </span>
          </div>
          <div>
            <strong>{pendingPackages.length}</strong>
            <span>{pendingPackages.length === 1 ? 'pacote em revisão' : 'pacotes em revisão'}</span>
          </div>
          <div>
            <strong>{operations?.results.publications ?? '—'}</strong>
            <span>
              {operations?.results.publications === 1
                ? 'publicação acompanhada'
                : 'publicações acompanhadas'}
            </span>
          </div>
        </div>
      )}
      <nav className="cw-tabs" aria-label="Seções da campanha">
        {(
          [
            { id: 'contents', label: 'Conteúdos', icon: Layers },
            { id: 'production', label: 'Produção', icon: ClipboardList },
            { id: 'materials', label: 'Materiais e aprovação', icon: CheckCheck },
            { id: 'distribution', label: 'Destinos e colaboração', icon: ArrowUpRight },
            { id: 'automations', label: 'Automações', icon: Settings2 },
            { id: 'promotions', label: 'Impulsionamento', icon: Megaphone },
            { id: 'results', label: 'Resultados', icon: Target },
          ] as const
        ).map((item) => (
          <button
            key={item.id}
            className={tab === item.id ? 'active' : ''}
            aria-current={tab === item.id ? 'page' : undefined}
            onClick={() => setTab(item.id)}
          >
            <item.icon size={17} />
            {item.label}
          </button>
        ))}
      </nav>
      {!editor && error && (
        <div className="cw-error" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="cw-success" role="status">
          <Check size={16} />
          {notice}
        </div>
      )}
      {loading && !operations ? (
        <div className="cw-empty" role="status">
          <Loader2 className="spin" size={22} />
          Carregando operação da campanha…
        </div>
      ) : (
        <>
          {tab === 'contents' && (
            <section className="cw-section" aria-labelledby="cw-contents-heading">
              <div className="cw-section-heading">
                <div>
                  <h2 id="cw-contents-heading">
                    {hasWork ? 'Conteúdos da campanha' : 'Qual é o próximo passo?'}
                  </h2>
                  <p>
                    {hasWork
                      ? 'Abra um conteúdo para acompanhar sua produção e publicações.'
                      : 'Sua campanha está pronta para receber conteúdo. Escolha por onde começar.'}
                  </p>
                </div>
                {canEdit && hasWork && (
                  <div className="cw-actions cw-content-actions">
                    <button
                      disabled={
                        !data.contents.some((content) => !content.campaignIds.includes(campaign.id))
                      }
                      onClick={() => open({ kind: 'link' })}
                    >
                      Vincular peça existente
                    </button>
                    <button className="primary" onClick={onNewContent}>
                      <Plus size={16} />
                      Nova peça
                    </button>
                  </div>
                )}
              </div>
              {contents.length ? (
                <div className="cw-content-grid">
                  {contents.map((content) => {
                    const date = calendar.find((entry) => entry.contentId === content.id)?.date;
                    const publications = data.publications.filter(
                      (publication) => publication.contentId === content.id,
                    );
                    const firstPublication = [...publications].sort((a, b) =>
                      (a.publishedAt || a.date).localeCompare(b.publishedAt || b.date),
                    )[0];
                    const tasks = activeTasks.filter((task) => task.contentId === content.id);
                    const approved = operations?.packages.some(
                      (item) => item.contentId === content.id && item.status === 'aprovada',
                    );
                    return (
                      <button
                        key={content.id}
                        className="cw-content-card"
                        onClick={() => onContent(content)}
                      >
                        <div className="cw-content-card-top">
                          <FormatBadge format={content.format} />
                          <span className={`badge ${content.stage}`}>
                            {stageLabels[content.stage]}
                          </span>
                        </div>
                        <h3>{contentName(content)}</h3>
                        <p>{content.brief || 'Abra a peça para desenvolver o briefing.'}</p>
                        <div className="cw-content-detail">
                          <span>
                            <CalendarDays size={13} />
                            {firstPublication
                              ? `Publicado em ${firstPublication.precision === 'second' && firstPublication.publishedAt ? stamp(firstPublication.publishedAt) : dateLabel(firstPublication.date)}`
                              : date
                                ? `Previsto para ${dateLabel(date)}`
                                : 'Sem data planejada'}
                          </span>
                          <span>{content.owner || 'Sem responsável'}</span>
                        </div>
                        <div className="cw-content-footer">
                          <span>
                            {publications.length
                              ? `${publications.length} ${publications.length === 1 ? 'publicação' : 'publicações'}`
                              : approved
                                ? 'Pacote aprovado'
                                : `${tasks.length} tarefa(s) aberta(s)`}
                          </span>
                          <ChevronRight size={15} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : !hasWork ? (
                <div className="cw-start-grid">
                  <article>
                    <div className="cw-start-icon">
                      <ArrowUpRight size={22} />
                    </div>
                    <h3>Já publiquei um vídeo</h3>
                    <p>
                      Escolha um vídeo da sua conta do Instagram e acompanhe seus resultados nesta
                      campanha.
                    </p>
                    {canEdit && (
                      <button className="primary" onClick={onAddPublication}>
                        Adicionar vídeo do Instagram
                      </button>
                    )}
                  </article>
                  <article>
                    <div className="cw-start-icon">
                      <FileText size={22} />
                    </div>
                    <h3>Vou produzir conteúdo</h3>
                    <p>
                      Crie a pauta e organize roteiro, produção, aprovação e data de publicação.
                    </p>
                    {canEdit && (
                      <button onClick={onNewContent}>
                        <Plus size={16} />
                        Nova peça
                      </button>
                    )}
                  </article>
                  {canEdit && (
                    <p className="cw-start-existing">
                      Já existe no acervo?{' '}
                      <button
                        className="text-button"
                        onClick={() => open({ kind: 'link' })}
                        disabled={
                          !data.contents.some(
                            (content) => !content.campaignIds.includes(campaign.id),
                          )
                        }
                      >
                        Vincular peça existente
                      </button>
                    </p>
                  )}
                </div>
              ) : (
                <Empty>
                  Nenhum conteúdo inteiro está vinculado ao planejamento desta campanha.
                </Empty>
              )}
              {publicationOnly.length > 0 && (
                <div className="cw-publication-only">
                  <h3>Publicações específicas</h3>
                  <p>
                    Somente estas publicações participam da campanha; os demais conteúdos dessas
                    peças mantêm seus vínculos originais.
                  </p>
                  <div className="cw-publication-grid">
                    {publicationOnly.map((media) => (
                      <article key={media.id}>
                        <strong>{titleFor(media.contentId)}</strong>
                        <span>
                          {media.account} ·{' '}
                          {media.precision === 'day'
                            ? dateLabel(media.publishedAt)
                            : new Date(media.publishedAt).toLocaleDateString('pt-BR', {
                                timeZone: 'America/Sao_Paulo',
                              })}
                        </span>
                        <ExternalLink url={media.url}>Ver publicação</ExternalLink>
                      </article>
                    ))}
                  </div>
                  <button onClick={() => setTab('results')}>Acompanhar resultados</button>
                </div>
              )}
            </section>
          )}

          {tab === 'production' && (
            <section className="cw-section" aria-labelledby="cw-production-heading">
              <div className="cw-section-heading">
                <div>
                  <h2 id="cw-production-heading">Entregas da equipe</h2>
                  <p>
                    Organize roteiro, gravação, edição e revisão com prazo e evidência de conclusão.
                  </p>
                </div>
                {canEdit && (
                  <button className="primary" onClick={() => open({ kind: 'task' })}>
                    <Plus size={16} />
                    Nova tarefa
                  </button>
                )}
              </div>
              <div className="cw-filter">
                <label htmlFor="cw-task-filter">Exibir</label>
                <select
                  id="cw-task-filter"
                  value={taskFilter}
                  onChange={(event) => setTaskFilter(event.target.value)}
                >
                  <option value="open">Tarefas abertas</option>
                  <option value="all">Todas as tarefas</option>
                  <option value="overdue">Em atraso</option>
                  <option value="concluida">Concluídas</option>
                </select>
              </div>
              <div className="cw-list">
                {(operations?.tasks ?? [])
                  .filter(
                    (task) =>
                      taskFilter === 'all' ||
                      (taskFilter === 'open'
                        ? !['concluida', 'cancelada'].includes(task.status)
                        : taskFilter === 'overdue'
                          ? overdue.some((item) => item.id === task.id)
                          : task.status === taskFilter),
                  )
                  .sort((a, b) => (a.dueDate || '9999').localeCompare(b.dueDate || '9999'))
                  .map((task) => (
                    <article key={task.id} className="cw-task">
                      <div className={`cw-task-mark ${task.status === 'concluida' ? 'done' : ''}`}>
                        <Check size={17} />
                      </div>
                      <div className="cw-task-main">
                        <h3>{task.title}</h3>
                        <p>{titleFor(task.contentId)}</p>
                        {task.evidence && (
                          <details>
                            <summary>Evidência de entrega</summary>
                            <p className="cw-prewrap">{task.evidence}</p>
                          </details>
                        )}
                      </div>
                      <div className="cw-task-meta">
                        <Status value={task.status}>{taskLabels[task.status]}</Status>
                        <span>{task.owner || 'Sem responsável'}</span>
                        <span
                          className={overdue.some((item) => item.id === task.id) ? 'cw-late' : ''}
                        >
                          {dateLabel(task.dueDate)}
                          {overdue.some((item) => item.id === task.id) ? ' · em atraso' : ''}
                        </span>
                      </div>
                      {canEdit && (
                        <button onClick={() => open({ kind: 'task', item: task })}>
                          Editar tarefa
                        </button>
                      )}
                    </article>
                  ))}
              </div>
              {!(operations?.tasks ?? []).some(
                (task) =>
                  taskFilter === 'all' ||
                  (taskFilter === 'open'
                    ? !['concluida', 'cancelada'].includes(task.status)
                    : taskFilter === 'overdue'
                      ? overdue.some((item) => item.id === task.id)
                      : task.status === taskFilter),
              ) && (
                <Empty>
                  {taskFilter === 'all'
                    ? 'Crie as tarefas de produção desta campanha e distribua as entregas.'
                    : 'Nenhuma tarefa neste filtro.'}
                </Empty>
              )}
            </section>
          )}

          {tab === 'materials' && (
            <div className="cw-material-layout">
              <section className="cw-section" aria-labelledby="cw-materials-heading">
                <div className="cw-section-heading">
                  <div>
                    <h2 id="cw-materials-heading">Materiais da campanha</h2>
                    <p>Roteiros, legendas e mídias com versões preservadas.</p>
                  </div>
                  {canEdit && (
                    <button disabled={!contents.length} onClick={() => open({ kind: 'material' })}>
                      <Plus size={16} />
                      Adicionar material
                    </button>
                  )}
                </div>
                {!contents.length && (
                  <div className="cw-info">
                    Crie uma peça na aba Conteúdos para vincular seus materiais.
                  </div>
                )}
                {currentMaterials.length ? (
                  <div className="cw-material-list">
                    {currentMaterials.map((material) => (
                      <article className="cw-material" key={material.id}>
                        <div className="cw-material-title">
                          <FileText size={18} />
                          <div>
                            <h3>{material.title}</h3>
                            <p>
                              {materialLabels[material.kind]} · {titleFor(material.contentId)}
                            </p>
                          </div>
                        </div>
                        {material.text && (
                          <details>
                            <summary>Ver texto</summary>
                            <p className="cw-material-text">{material.text}</p>
                          </details>
                        )}
                        <div className="cw-material-links">
                          {material.fileName && material.size > 0 && (
                            <a
                              href={`/api/materials/${encodeURIComponent(material.id)}/file`}
                              download
                            >
                              <ArrowDownToLine size={14} />
                              {material.fileName}
                            </a>
                          )}
                          {material.url && (
                            <ExternalLink url={material.url}>
                              Abrir arquivo de referência
                            </ExternalLink>
                          )}
                        </div>
                        <div className="cw-record-foot">
                          <span>
                            {stamp(material.createdAt)}
                            {material.replacesId ? ' · nova versão' : ''}
                          </span>
                          {canEdit && (
                            <button
                              className="text-button"
                              onClick={() => open({ kind: 'material', item: material })}
                            >
                              Criar revisão
                            </button>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <Empty>Adicione o roteiro, a legenda ou o arquivo final de cada peça.</Empty>
                )}
                {materials.length > currentMaterials.length && (
                  <details className="cw-history">
                    <summary>
                      Versões anteriores ({materials.length - currentMaterials.length})
                    </summary>
                    {materials
                      .filter((material) => replaced.has(material.id))
                      .map((material) => (
                        <div className="cw-history-entry" key={material.id}>
                          <strong>{material.title}</strong>
                          <span>
                            {materialLabels[material.kind]} · {stamp(material.createdAt)}
                          </span>
                          {material.text && (
                            <details>
                              <summary>Texto preservado</summary>
                              <p className="cw-material-text">{material.text}</p>
                            </details>
                          )}
                          {material.fileName && material.size > 0 && (
                            <a
                              href={`/api/materials/${encodeURIComponent(material.id)}/file`}
                              download
                            >
                              Baixar versão anterior
                            </a>
                          )}
                          {material.url && (
                            <ExternalLink url={material.url}>Referência da versão</ExternalLink>
                          )}
                        </div>
                      ))}
                  </details>
                )}
              </section>
              <section className="cw-section" aria-labelledby="cw-packages-heading">
                <div className="cw-section-heading">
                  <div>
                    <h2 id="cw-packages-heading">Pacotes de publicação</h2>
                    <p>Aprovação reúne a versão dos materiais, legenda, canais, CTA e data.</p>
                  </div>
                  {canEdit && (
                    <button
                      className="primary"
                      disabled={!contents.length}
                      onClick={() => open({ kind: 'package' })}
                    >
                      <Plus size={16} />
                      Enviar para revisão
                    </button>
                  )}
                </div>
                {operations?.packages.length ? (
                  <div className="cw-package-list">
                    {[...operations.packages]
                      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
                      .map((pkg) => (
                        <article
                          className={`cw-package ${pkg.status === 'substituida' ? 'cw-old-package' : ''}`}
                          key={pkg.id}
                        >
                          <div className="cw-package-title">
                            <div>
                              <span className="cw-kicker">REVISÃO {pkg.revision}</span>
                              <h3>{titleFor(pkg.contentId)}</h3>
                            </div>
                            <Status value={pkg.status}>{packageLabels[pkg.status]}</Status>
                          </div>
                          <div className="cw-package-meta">
                            <span>
                              <CalendarDays size={13} />
                              {dateLabel(pkg.date)}
                              {pkg.plannedTime ? ` às ${pkg.plannedTime} · São Paulo` : ''}
                            </span>
                            <span>{pkg.destinations.join(' · ')}</span>
                          </div>
                          <details>
                            <summary>Ver pacote enviado</summary>
                            <dl className="cw-package-details">
                              <dt>Legenda</dt>
                              <dd className="cw-prewrap">{pkg.caption || 'Sem legenda'}</dd>
                              <dt>CTA aprovado para revisão</dt>
                              <dd>{pkg.cta || 'Sem CTA'}</dd>
                              <dt>Formato</dt>
                              <dd>{pkg.contentFormat}</dd>
                              <dt>Destinos e colaboradores</dt>
                              <dd>
                                {pkg.structuredDestinations?.map((destination) => (
                                  <div key={destination.id}>
                                    {destination.channel} · {destination.account}
                                    {destination.collaborators.length
                                      ? ` · Colaboradores planejados: ${destination.collaborators.join(', ')}`
                                      : ' · Sem colaboradores planejados'}
                                  </div>
                                )) ?? pkg.destinations.join(' · ')}
                              </dd>
                              <dt>Materiais desta revisão</dt>
                              <dd>
                                {pkg.materialIds.map((id) => {
                                  const material = materials.find((item) => item.id === id);
                                  return (
                                    <div key={id}>
                                      {material?.title || id}
                                      {material && (
                                        <span className="muted">
                                          {' '}
                                          · {materialLabels[material.kind]} ·{' '}
                                          {stamp(material.createdAt)}
                                        </span>
                                      )}
                                      {material?.fileName && material.size > 0 && (
                                        <a
                                          href={`/api/materials/${encodeURIComponent(id)}/file`}
                                          download
                                        >
                                          {' '}
                                          Baixar
                                        </a>
                                      )}
                                      {material?.text && (
                                        <details>
                                          <summary>Texto desta versão</summary>
                                          <p className="cw-material-text">{material.text}</p>
                                        </details>
                                      )}
                                      {material?.url && (
                                        <ExternalLink url={material.url}>
                                          Abrir referência
                                        </ExternalLink>
                                      )}
                                    </div>
                                  );
                                })}
                              </dd>
                              {pkg.editorialNote && (
                                <>
                                  <dt>Observação editorial</dt>
                                  <dd>{pkg.editorialNote}</dd>
                                </>
                              )}
                            </dl>
                          </details>
                          <PackageRevisionDiff
                            pkg={pkg}
                            packages={operations.packages}
                            materials={materials}
                          />
                          <PackageCtaReview campaign={campaign} pkg={pkg} materials={materials} />
                          {pkg.decisionNote && (
                            <div className="cw-decision">
                              <strong>{packageLabels[pkg.status]}</strong>
                              <p>{pkg.decisionNote}</p>
                              <span>
                                {pkg.decidedBy} · {stamp(pkg.decidedAt)}
                              </span>
                            </div>
                          )}
                          <div className="cw-package-actions">
                            {canApprove && pkg.status === 'em_revisao' && (
                              <>
                                <button
                                  className="cw-approve"
                                  disabled={
                                    busy ||
                                    assessPackageCta(campaign, pkg, materials).status === 'blocked'
                                  }
                                  title={
                                    assessPackageCta(campaign, pkg, materials).status === 'blocked'
                                      ? 'Revise o pedido de direct antes de aprovar.'
                                      : undefined
                                  }
                                  onClick={() =>
                                    open({ kind: 'decision', item: pkg, decision: 'aprovada' })
                                  }
                                >
                                  <CheckCheck size={15} />
                                  Aprovar pacote
                                </button>
                                <button
                                  disabled={busy}
                                  onClick={() =>
                                    open({ kind: 'decision', item: pkg, decision: 'ajustes' })
                                  }
                                >
                                  Pedir ajustes
                                </button>
                                <button
                                  className="text-button"
                                  disabled={busy}
                                  onClick={() =>
                                    open({ kind: 'decision', item: pkg, decision: 'recusada' })
                                  }
                                >
                                  Recusar
                                </button>
                              </>
                            )}
                            {canEdit &&
                              !operations.packages.some(
                                (other) =>
                                  other.contentId === pkg.contentId &&
                                  other.revision > pkg.revision,
                              ) && (
                                <button
                                  className="text-button"
                                  onClick={() => open({ kind: 'package', item: pkg })}
                                >
                                  Nova revisão do pacote
                                </button>
                              )}
                          </div>
                          <p className="cw-package-foot">
                            Enviado em {stamp(pkg.createdAt)}
                            {pkg.status === 'aprovada'
                              ? ' · aprovação não registra publicação'
                              : ''}
                          </p>
                        </article>
                      ))}
                  </div>
                ) : (
                  <Empty>
                    Selecione materiais de uma peça e envie o pacote ao aprovador da campanha.
                  </Empty>
                )}
              </section>
            </div>
          )}

          {tab === 'distribution' && (
            <DistributionWorkspace
              campaignId={campaign.id}
              initialPackageId={initialPackageId}
              data={data}
              refresh={async () => {
                await Promise.all([load(), refresh()]);
              }}
            />
          )}
          {tab === 'promotions' && (
            <>
              <AdsWorkspace campaignId={campaign.id} data={data} />
              <PromotionWorkspace campaign={campaign} data={data} />
            </>
          )}
          {tab === 'automations' && (
            <AutomationWorkspace
              campaignId={campaign.id}
              initialAutomationId={initialAutomationId}
              data={data}
              refresh={refresh}
            />
          )}
          {tab === 'results' && operations && (
            <>
              <section className="cw-section" aria-labelledby="cw-results-heading">
                <div className="cw-section-heading">
                  <div>
                    <h2 id="cw-results-heading">Resultados da campanha</h2>
                    <p>
                      Publicações registradas, leituras da Meta e eventos comerciais informados pela
                      equipe.
                    </p>
                  </div>
                  <a
                    className="button"
                    href={`/api/campaigns/${encodeURIComponent(campaign.id)}/results.csv`}
                    download
                  >
                    <ArrowDownToLine size={16} />
                    Exportar CSV
                  </a>
                </div>
                <div className="cw-result-cards">
                  <div>
                    <span>Conteúdos com publicação</span>
                    <strong>{count(operations.results.publishedPieces)}</strong>
                    <small>Nas publicações acompanhadas nesta campanha</small>
                  </div>
                  <div>
                    <span>Publicações por conta</span>
                    <strong>{count(operations.results.publications)}</strong>
                  </div>
                  <div>
                    <span>Interações medidas</span>
                    <strong>
                      {operations.results.interactions === null
                        ? '—'
                        : count(operations.results.interactions)}
                    </strong>
                    <small>
                      {Math.round(operations.results.interactionCoverage * 100)}% das publicações
                      com interações disponíveis
                    </small>
                  </div>
                  <div>
                    <span>Cobertura de leitura</span>
                    <strong>
                      {count(operations.results.measuredPublications)}
                      <small> / {count(operations.results.publications)}</small>
                    </strong>
                    <small>publicações com leitura da Meta</small>
                  </div>
                </div>
                <div className="cw-info">
                  As leituras da Meta mostram o total da publicação, sem separar o impulsionamento.
                  Registre o resultado pago na aba Impulsionamento. Alcance é mostrado por
                  publicação; não é somado porque uma mesma pessoa pode ver várias peças. As
                  leituras têm datas próprias e não representam automaticamente a janela de sete
                  dias. Os eventos comerciais contam registros por tipo, não pessoas únicas.
                </div>
                {collectionSettings && (
                  <details className="cw-collection">
                    <summary>Coleta de métricas</summary>
                    {collectionSettings}
                  </details>
                )}
                <div className="cw-event-counts">
                  {Object.entries(eventLabels).map(([key, label]) => (
                    <div key={key}>
                      <strong>
                        {count(operations.results.events[key as BusinessEvent['kind']])}
                      </strong>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
                <div className="cw-subheading">
                  <h3>Metas da campanha</h3>
                  {canEdit && (
                    <button onClick={() => open({ kind: 'goal' })}>
                      <Plus size={14} />
                      Definir meta
                    </button>
                  )}
                </div>
                {operations.results.goals.length ? (
                  <div className="cw-goals">
                    {operations.results.goals.map((goal) => (
                      <div className="cw-goal" key={goal.id}>
                        <div>
                          <strong>
                            {goal.metric === 'publicacoes'
                              ? 'Publicações'
                              : eventLabels[goal.metric]}
                          </strong>
                          <span>
                            {count(goal.actual)} de {count(goal.target)}
                          </span>
                        </div>
                        <progress
                          max={goal.target}
                          value={Math.min(goal.actual, goal.target)}
                          aria-label={`Meta de ${goal.metric === 'publicacoes' ? 'publicações' : eventLabels[goal.metric]}`}
                        />
                        {canEdit && (
                          <button
                            className="text-button"
                            onClick={() => open({ kind: 'goal', item: goal })}
                          >
                            Editar meta
                          </button>
                        )}
                        {canEdit && (
                          <button
                            className="text-button"
                            disabled={busy}
                            onClick={() =>
                              void save(
                                () =>
                                  mutate(`/api/goals/${encodeURIComponent(goal.id)}`, 'DELETE', {
                                    version: goal.version,
                                  }),
                                'Meta removida.',
                              )
                            }
                          >
                            Remover meta
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="cw-empty-line">
                    Defina metas para comparar o resultado com o objetivo da campanha.
                  </p>
                )}
                <div className="cw-subheading">
                  <h3>Leituras por publicação</h3>
                  <span>Atualizado em {stamp(operations.results.generatedAt)}</span>
                </div>
                {operations.results.media.length ? (
                  <div className="cw-media-results">
                    {operations.results.media.map((media) => (
                      <article key={media.id}>
                        <div className="cw-reading-heading">
                          <div>
                            <strong>{titleFor(media.contentId)}</strong>
                            <p>
                              {media.account} ·{' '}
                              {media.platform === 'instagram'
                                ? 'Instagram'
                                : media.platform === 'facebook'
                                  ? 'Facebook'
                                  : media.platform}
                            </p>
                          </div>
                          <ExternalLink url={media.url}>Ver publicação</ExternalLink>
                        </div>
                        <div className="cw-metric-list">
                          {media.metrics.length ? (
                            media.metrics.map((metric) => (
                              <div key={metric.key}>
                                <span>{metric.label}</span>
                                <strong>
                                  {metric.status === 'available' && metric.value !== null
                                    ? `${count(metric.value)}${metric.unit === 'milliseconds' ? ' ms' : ''}`
                                    : 'Indisponível'}
                                </strong>
                                {metric.status === 'error' && <small>Falha na leitura</small>}
                              </div>
                            ))
                          ) : (
                            <p className="muted">Sem métricas disponíveis.</p>
                          )}
                        </div>
                        <p className="cw-reading-foot">
                          Publicada em{' '}
                          {media.precision === 'day' ||
                          /^\d{4}-\d{2}-\d{2}$/.test(media.publishedAt)
                            ? dateLabel(media.publishedAt)
                            : stamp(media.publishedAt)}{' '}
                          · Leitura:{' '}
                          {media.collectedAt ? stamp(media.collectedAt) : 'sem leitura vinculada'} ·
                          Fonte:{' '}
                          {media.provenance === 'archive'
                            ? 'registro do acervo'
                            : media.source === 'manual'
                              ? 'registro manual'
                              : media.source === 'api'
                                ? 'publicação simulada (API)' // só existe gateway simulado
                                : 'Meta'}
                        </p>
                        <ReadingHistory readings={media.readings ?? []} />
                      </article>
                    ))}
                  </div>
                ) : (
                  <Empty>
                    Vincule as publicações da campanha na área Publicações para acompanhar as
                    leituras da Meta.
                  </Empty>
                )}
              </section>
              <ManualMetricsWorkspace campaignId={campaign.id} data={data} refresh={refresh} />
              <ArchiveHistoryWorkspace results={operations.results} titleFor={titleFor} />
              <section className="cw-section" aria-labelledby="cw-events-heading">
                <div className="cw-section-heading">
                  <div>
                    <h2 id="cw-events-heading">Eventos comerciais</h2>
                    <p>Registre pedidos, conversas e oportunidades com origem e evidência.</p>
                  </div>
                  {canEdit && (
                    <button className="primary" onClick={() => open({ kind: 'event' })}>
                      <Plus size={16} />
                      Registrar evento
                    </button>
                  )}
                </div>
                {operations.events.length ? (
                  <div className="cw-events">
                    {[...operations.events]
                      .sort((a, b) => b.date.localeCompare(a.date))
                      .map((event) => (
                        <article
                          key={event.id}
                          className={event.status === 'anulado' ? 'cw-void-event' : ''}
                        >
                          <div>
                            <div className="cw-event-heading">
                              <h3>{event.contact}</h3>
                              <Status value={event.status}>
                                {event.status === 'anulado' ? 'Anulado' : eventLabels[event.kind]}
                              </Status>
                            </div>
                            <p>
                              {event.channel} · {dateLabel(event.date)} ·{' '}
                              {titleFor(event.contentId)}
                            </p>
                            <details>
                              <summary>Dados e evidência</summary>
                              <p className="cw-prewrap">{event.evidence}</p>
                              <p>
                                {event.rooms === null
                                  ? 'Consultórios não informados'
                                  : `${event.rooms} consultórios`}{' '}
                                ·{' '}
                                {event.customer === 'sim'
                                  ? 'Já é cliente'
                                  : event.customer === 'nao'
                                    ? 'Ainda não é cliente'
                                    : 'Vínculo não informado'}
                              </p>
                              <p>
                                Link enviado: {event.linkSent ? 'sim' : 'não'} · Permissão para
                                contato: {event.contactPermission ? 'sim' : 'não'}
                              </p>
                            </details>
                          </div>
                          {canEdit && (
                            <button onClick={() => open({ kind: 'event', item: event })}>
                              Editar evento
                            </button>
                          )}
                        </article>
                      ))}
                  </div>
                ) : (
                  <Empty>
                    Os eventos aparecerão aqui com os dados que a equipe registrar. Eventos anulados
                    permanecem no histórico.
                  </Empty>
                )}
              </section>
            </>
          )}
        </>
      )}

      {editor && (
        <Dialog
          title={
            editor.kind === 'task'
              ? editor.item
                ? 'Editar tarefa'
                : 'Nova tarefa'
              : editor.kind === 'material'
                ? editor.item
                  ? 'Criar revisão do material'
                  : 'Adicionar material'
                : editor.kind === 'package'
                  ? editor.item
                    ? 'Nova revisão do pacote'
                    : 'Enviar pacote para revisão'
                  : editor.kind === 'decision'
                    ? editor.decision === 'aprovada'
                      ? 'Aprovar pacote'
                      : editor.decision === 'ajustes'
                        ? 'Pedir ajustes'
                        : 'Recusar pacote'
                    : editor.kind === 'event'
                      ? editor.item
                        ? 'Editar evento comercial'
                        : 'Registrar evento comercial'
                      : editor.kind === 'link'
                        ? 'Vincular peça existente'
                        : 'Definir meta da campanha'
          }
          busy={busy}
          onClose={() => setEditor(undefined)}
        >
          {error && (
            <div className="cw-error" role="alert">
              {error}
            </div>
          )}
          {editor.kind === 'link' && (
            <form
              onSubmit={submit((form) => {
                const content = data.contents.find((item) => item.id === s(form, 'contentId'));
                if (!content) return Promise.reject(new Error('Selecione uma peça existente.'));
                return mutate(`/api/contents/${encodeURIComponent(content.id)}`, 'PUT', {
                  title: content.title,
                  format: content.format,
                  stage: content.stage,
                  owner: content.owner,
                  brief: content.brief,
                  notes: content.notes,
                  campaignIds: [...new Set([...content.campaignIds, campaign.id])],
                  reserve: content.reserve,
                  stageEvidence: content.stageEvidence,
                  version: content.version,
                });
              }, 'Peça vinculada à campanha.')}
            >
              <fieldset disabled={busy} className="cw-fields">
                <Field label="Peça existente" wide>
                  <select name="contentId" required autoFocus defaultValue="">
                    <option value="" disabled>
                      Selecione uma peça do acervo
                    </option>
                    {data.contents
                      .filter((content) => !content.campaignIds.includes(campaign.id))
                      .map((content) => (
                        <option value={content.id} key={content.id}>
                          {contentName(content)} · {stageLabels[content.stage]}
                        </option>
                      ))}
                  </select>
                </Field>
                <div className="cw-info wide">
                  A campanha passa a referenciar esta peça. Seus materiais, histórico e vínculos com
                  outras campanhas são preservados.
                </div>
              </fieldset>
              {buttons('Vincular peça')}
            </form>
          )}
          {editor.kind === 'task' && (
            <form
              onSubmit={submit(
                (form) =>
                  mutate(
                    editor.item ? `/api/tasks/${encodeURIComponent(editor.item.id)}` : '/api/tasks',
                    editor.item ? 'PUT' : 'POST',
                    {
                      campaignId: campaign.id,
                      contentId: s(form, 'contentId') || null,
                      title: s(form, 'title'),
                      owner: s(form, 'owner'),
                      dueDate: s(form, 'dueDate'),
                      status: s(form, 'status'),
                      evidence: s(form, 'evidence'),
                      ...(editor.item ? { version: editor.item.version } : {}),
                    },
                  ),
                'Tarefa salva.',
              )}
            >
              <fieldset disabled={busy} className="cw-fields">
                <Field label="Entrega" wide>
                  <input
                    name="title"
                    defaultValue={editor.item?.title}
                    placeholder="Ex.: editar o Reel do guia"
                    required
                    maxLength={200}
                    autoFocus
                  />
                </Field>
                <Field label="Peça" wide>
                  <select
                    name="contentId"
                    defaultValue={editor.item?.contentId || ''}
                    disabled={Boolean(editor.item)}
                  >
                    <option value="">Tarefa geral da campanha</option>
                    {contentOptions}
                  </select>
                  {editor.item && (
                    <input type="hidden" name="contentId" value={editor.item.contentId || ''} />
                  )}
                </Field>
                <Field label="Responsável">
                  <input
                    name="owner"
                    defaultValue={editor.item?.owner || campaign.owner}
                    list="cw-members"
                    required
                    maxLength={120}
                  />
                </Field>
                <Field label="Prazo">
                  <input type="date" name="dueDate" defaultValue={editor.item?.dueDate} required />
                </Field>
                <Field label="Status da tarefa" wide>
                  <select name="status" defaultValue={editor.item?.status || 'pendente'}>
                    {Object.entries(taskLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Evidência da entrega" wide>
                  <textarea
                    name="evidence"
                    rows={3}
                    defaultValue={editor.item?.evidence}
                    placeholder="Arquivo entregue, link ou descrição da conclusão"
                  />
                </Field>
              </fieldset>
              {buttons('Salvar tarefa')}
            </form>
          )}
          {editor.kind === 'material' && (
            <form onSubmit={submit(saveMaterial, 'Material salvo com sua versão preservada.')}>
              <fieldset disabled={busy} className="cw-fields">
                {editor.item && (
                  <div className="cw-info wide">
                    Esta revisão preserva a versão anterior. Pacotes que usam a versão alterada
                    precisam de uma nova revisão.
                  </div>
                )}
                <Field label="Título do material" wide>
                  <input
                    name="title"
                    required
                    maxLength={200}
                    defaultValue={editor.item?.title}
                    autoFocus
                  />
                </Field>
                <Field label="Peça" wide>
                  <select
                    name="contentId"
                    value={selectedContent}
                    disabled={Boolean(editor.item)}
                    onChange={(event) => setSelectedContent(event.target.value)}
                    required
                  >
                    {contentOptions}
                  </select>
                  {editor.item && (
                    <input type="hidden" name="contentId" value={editor.item.contentId} />
                  )}
                </Field>
                <Field label="Tipo de material" wide>
                  <select
                    name="kind"
                    value={materialKind}
                    disabled={Boolean(editor.item)}
                    onChange={(event) => setMaterialKind(event.target.value as Material['kind'])}
                  >
                    {Object.entries(materialLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {editor.item && <input type="hidden" name="kind" value={editor.item.kind} />}
                </Field>
                <Field label="Texto do material" wide>
                  <textarea
                    name="text"
                    rows={7}
                    defaultValue={editor.item?.text}
                    required={materialKind !== 'midia'}
                    placeholder={
                      materialKind === 'roteiro'
                        ? 'Roteiro completo desta versão'
                        : materialKind === 'legenda'
                          ? 'Legenda completa desta versão'
                          : 'Descrição ou observações do arquivo'
                    }
                  />
                </Field>
                <Field label="Arquivo (até 15 MB)" wide>
                  <input
                    name="file"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,application/pdf,video/mp4,video/quicktime,audio/mpeg,text/plain,.mov,.mp3,.txt"
                  />
                  <small>
                    PNG, JPEG, WebP, PDF, MP4, MOV, MP3 ou TXT. Use arquivo até 15 MB ou link HTTPS
                    com SHA-256 da versão.
                  </small>
                </Field>
                <Field label="URL do arquivo" wide>
                  <input
                    name="url"
                    type="url"
                    defaultValue={editor.item?.url}
                    placeholder="https://…"
                  />
                </Field>
                <Field label="SHA-256 do arquivo de referência" wide>
                  <input
                    name="sha256"
                    defaultValue={editor.item?.url ? editor.item.sha256 : ''}
                    pattern="[a-fA-F0-9]{64}"
                    placeholder="64 caracteres, obrigatório para mídia por URL"
                  />
                </Field>
                <Field label="Nome do arquivo de referência" wide>
                  <input
                    name="fileName"
                    defaultValue={editor.item?.url ? editor.item.fileName : ''}
                    placeholder="Ex.: reel-guia-v2.mp4"
                  />
                </Field>
              </fieldset>
              {buttons(editor.item ? 'Salvar nova versão' : 'Salvar material')}
            </form>
          )}
          {editor.kind === 'package' && (
            <form
              onSubmit={submit(
                (form) =>
                  mutate('/api/packages', 'POST', {
                    campaignId: campaign.id,
                    contentId: s(form, 'contentId'),
                    caption: s(form, 'caption'),
                    ctaException: s(form, 'ctaException') || null,
                    structuredDestinations: JSON.parse(s(form, 'structuredDestinations')).map(
                      (destination: {
                        channel: string;
                        account: string;
                        collaborators: string[];
                      }) => ({
                        ...destination,
                        account: destination.account.trim(),
                        collaborators: destination.collaborators
                          .map((profile) => profile.trim())
                          .filter(Boolean),
                      }),
                    ),
                    plannedTime: s(form, 'plannedTime'),
                    plannedTimezone: 'America/Sao_Paulo',
                    materialIds: form.getAll('materialIds').map(String),
                    editorialNote: s(form, 'editorialNote'),
                  }),
                'Pacote enviado para revisão.',
              )}
            >
              <fieldset disabled={busy} className="cw-fields">
                <Field label="Peça" wide>
                  <select
                    name="contentId"
                    value={selectedContent}
                    onChange={(event) => setSelectedContent(event.target.value)}
                    required
                  >
                    {contentOptions}
                  </select>
                </Field>
                <div className="cw-info wide">
                  Data:{' '}
                  {dateLabel(
                    calendar.find((entry) => entry.contentId === selectedContent)?.date || '',
                  )}{' '}
                  · CTA: {campaign.cta || 'A definir na campanha'}. Estes dados ficam registrados
                  nesta revisão.
                </div>
                <Field label="Legenda do pacote" wide>
                  <textarea name="caption" defaultValue={editor.item?.caption} rows={5} required />
                </Field>
                <Field label="Exceção editorial de CTA" wide>
                  <select name="ctaException" defaultValue={editor.item?.ctaException ?? ''}>
                    <option value="">Sem exceção · CTA padrão por comentário</option>
                    {guideExceptionEligible(campaign) && (
                      <option value="guia_orcamentos">
                        GUIA no direct do @reinaldoferreiraoficial
                      </option>
                    )}
                  </select>
                  <small>
                    {guideExceptionEligible(campaign)
                      ? 'Declare a exceção quando o pedido for GUIA no direct do fundador. A decisão de aprovação vale para esta revisão.'
                      : 'A exceção GUIA pertence à campanha oficial do guia de orçamentos. Pedidos de direct em outras campanhas precisam de revisão.'}{' '}
                    Não é exigido aviso de suporte.
                  </small>
                </Field>
                <PackageDestinations item={editor.item} />
                <Field label="Horário planejado · São Paulo">
                  <input
                    type="time"
                    name="plannedTime"
                    defaultValue={editor.item?.plannedTime ?? ''}
                    disabled={!calendar.find((entry) => entry.contentId === selectedContent)?.date}
                  />
                  <small>
                    Opcional, quando a peça tem data definida. Faz parte da revisão para aprovação.
                  </small>
                </Field>
                <div className="wide cw-material-picker">
                  <span>Materiais incluídos</span>
                  {currentMaterials.filter((material) => material.contentId === selectedContent)
                    .length ? (
                    currentMaterials
                      .filter((material) => material.contentId === selectedContent)
                      .map((material) => (
                        <label key={material.id}>
                          <input
                            name="materialIds"
                            type="checkbox"
                            value={material.id}
                            defaultChecked={selectedInRevision(material, editor.item)}
                          />
                          <span>
                            {material.title}
                            <small>
                              {materialLabels[material.kind]} · {stamp(material.createdAt)}
                            </small>
                          </span>
                        </label>
                      ))
                  ) : (
                    <p>
                      Nenhum material atual para esta peça. Adicione os materiais antes de enviar.
                    </p>
                  )}
                </div>
                <Field label="Observação editorial" wide>
                  <textarea
                    name="editorialNote"
                    rows={2}
                    defaultValue={editor.item?.editorialNote}
                    required
                    minLength={3}
                    placeholder="Orientações para revisar esta publicação"
                  />
                </Field>
              </fieldset>
              {buttons('Enviar pacote')}
            </form>
          )}
          {editor.kind === 'decision' && (
            <form
              onSubmit={submit(
                (form) =>
                  mutate(`/api/packages/${encodeURIComponent(editor.item.id)}/decision`, 'POST', {
                    version: editor.item.version,
                    decision: editor.decision,
                    note: s(form, 'note'),
                    ...(form.has('ctaNotRequest')
                      ? {
                          ctaReview: {
                            decision: 'not_a_direct_request',
                            note: s(form, 'ctaReviewNote'),
                          },
                        }
                      : {}),
                  }),
                editor.decision === 'aprovada' ? 'Pacote aprovado.' : 'Decisão registrada.',
              )}
            >
              <fieldset disabled={busy} className="cw-fields">
                <div className="cw-info wide">
                  {titleFor(editor.item.contentId)} · revisão {editor.item.revision}. A decisão vale
                  para os materiais e dados deste pacote.
                </div>
                <div className="wide">
                  <PackageRevisionDiff
                    pkg={editor.item}
                    packages={operations?.packages ?? []}
                    materials={materials}
                  />
                  <PackageCtaReview campaign={campaign} pkg={editor.item} materials={materials} />
                </div>
                {editor.decision === 'aprovada' &&
                  assessPackageCta(campaign, editor.item, materials).status ===
                    'review_required' && (
                    <>
                      <label className="cw-cta-confirm wide">
                        <input type="checkbox" name="ctaNotRequest" required />
                        Conferi as menções sinalizadas: elas não convidam o público a enviar
                        mensagem no direct.
                      </label>
                      <Field label="Parecer sobre a menção a direct" wide>
                        <textarea
                          name="ctaReviewNote"
                          required
                          minLength={20}
                          rows={3}
                          placeholder="Explique a intenção e o contexto das menções sinalizadas."
                        />
                      </Field>
                    </>
                  )}
                <Field label="Parecer da revisão" wide>
                  <textarea
                    name="note"
                    rows={4}
                    required={editor.decision !== 'aprovada'}
                    minLength={editor.decision !== 'aprovada' ? 3 : undefined}
                    placeholder={
                      editor.decision === 'aprovada'
                        ? 'Observações da aprovação (opcional)'
                        : 'Explique os ajustes necessários ou o motivo da recusa'
                    }
                    autoFocus
                  />
                </Field>
              </fieldset>
              {buttons(
                editor.decision === 'aprovada' ? 'Confirmar aprovação' : 'Registrar decisão',
              )}
            </form>
          )}
          {editor.kind === 'event' && (
            <form
              onSubmit={submit(
                (form) =>
                  mutate(
                    editor.item
                      ? `/api/events/${encodeURIComponent(editor.item.id)}`
                      : '/api/events',
                    editor.item ? 'PUT' : 'POST',
                    {
                      campaignId: campaign.id,
                      contentId: s(form, 'contentId') || null,
                      kind: s(form, 'kind'),
                      contact: s(form, 'contact'),
                      channel: s(form, 'channel'),
                      date: s(form, 'date'),
                      evidence: s(form, 'evidence'),
                      rooms: s(form, 'rooms') ? Number(s(form, 'rooms')) : null,
                      customer: s(form, 'customer'),
                      linkSent: form.get('linkSent') === 'on',
                      contactPermission: form.get('contactPermission') === 'on',
                      status: s(form, 'status'),
                      ...(editor.item ? { version: editor.item.version } : {}),
                    },
                  ),
                'Evento comercial salvo.',
              )}
            >
              <fieldset disabled={busy} className="cw-fields">
                <Field label="Tipo de evento">
                  <select name="kind" defaultValue={editor.item?.kind || 'pedido'}>
                    {Object.entries(eventLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Data do evento">
                  <input
                    type="date"
                    name="date"
                    defaultValue={editor.item?.date.slice(0, 10) || data.today}
                    max={data.today}
                    required
                  />
                </Field>
                <Field label="Contato ou clínica" wide>
                  <input
                    name="contact"
                    defaultValue={editor.item?.contact}
                    required
                    maxLength={200}
                    autoFocus
                  />
                </Field>
                <Field label="Canal de origem">
                  <input
                    name="channel"
                    defaultValue={editor.item?.channel}
                    required
                    placeholder="Ex.: Instagram @controleodonto"
                    maxLength={160}
                  />
                </Field>
                <Field label="Peça de origem">
                  <select name="contentId" defaultValue={editor.item?.contentId || ''}>
                    <option value="">Campanha, sem peça específica</option>
                    {contentOptions}
                  </select>
                </Field>
                <div className="cw-info wide">
                  Para registrar um lead qualificado, confirme que a clínica tem 5 ou mais
                  consultórios e ainda não é cliente.
                </div>
                <Field label="Número de consultórios">
                  <input
                    name="rooms"
                    type="number"
                    min="0"
                    step="1"
                    defaultValue={editor.item?.rooms ?? ''}
                  />
                </Field>
                <Field label="Já é cliente?">
                  <select name="customer" defaultValue={editor.item?.customer || 'desconhecido'}>
                    <option value="desconhecido">Não informado</option>
                    <option value="sim">Sim</option>
                    <option value="nao">Não</option>
                  </select>
                </Field>
                <Field label="Evidência do evento" wide>
                  <textarea
                    name="evidence"
                    defaultValue={editor.item?.evidence}
                    required
                    minLength={3}
                    rows={3}
                    placeholder="Link, referência ou descrição verificável da conversa"
                  />
                </Field>
                <div className="wide cw-checkbox-row">
                  <label>
                    <input name="linkSent" type="checkbox" defaultChecked={editor.item?.linkSent} />
                    Link enviado
                  </label>
                  <label>
                    <input
                      name="contactPermission"
                      type="checkbox"
                      defaultChecked={editor.item?.contactPermission}
                    />
                    Permissão para contato registrada
                  </label>
                </div>
                <Field label="Status do evento" wide>
                  <select name="status" defaultValue={editor.item?.status || 'valido'}>
                    <option value="valido">Válido</option>
                    <option value="anulado">Anulado (preservar histórico)</option>
                  </select>
                </Field>
              </fieldset>
              {buttons('Salvar evento')}
            </form>
          )}
          {editor.kind === 'goal' && (
            <form
              onSubmit={submit(
                (form) =>
                  mutate('/api/goals', 'POST', {
                    campaignId: campaign.id,
                    metric: s(form, 'metric'),
                    target: Number(s(form, 'target')),
                    ...(operations?.goals.find((goal) => goal.metric === s(form, 'metric'))
                      ? {
                          version: operations.goals.find(
                            (goal) => goal.metric === s(form, 'metric'),
                          )!.version,
                        }
                      : {}),
                  }),
                'Meta definida.',
              )}
            >
              <fieldset disabled={busy} className="cw-fields">
                <Field label="Indicador da meta">
                  <select name="metric" defaultValue={editor.item?.metric || 'publicacoes'}>
                    <option value="publicacoes">Publicações</option>
                    {Object.entries(eventLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Quantidade esperada">
                  <input
                    name="target"
                    type="number"
                    min="1"
                    step="1"
                    max="1000000000"
                    defaultValue={editor.item?.target}
                    required
                    autoFocus
                  />
                </Field>
              </fieldset>
              {buttons('Salvar meta')}
            </form>
          )}
          <datalist id="cw-members">
            {data.members
              .filter((member) => member.active)
              .map((member) => (
                <option value={member.name} key={member.id} />
              ))}
          </datalist>
        </Dialog>
      )}
    </div>
  );
}
